import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Search,
  CheckCircle,
  AlertTriangle,
  XCircle,
  FileText,
  Music,
  ArrowLeft,
  Loader,
  Youtube
} from 'lucide-react';
import Navigation from '../components/Navigation/Navigation';
import apiClient from '../utils/apiClient';

interface MissingFilesResult {
  summary: {
    totalChecked: number;
    foundCount: number;
    questionableCount: number;
    missingCount: number;
    searchType: 'bulletin' | 'all';
    checkedAt: string;
  };
  found: SearchResult[];
  questionable: SearchResult[];
  missing: SearchResult[];
  services: ServiceInfo[];
  // Computed on frontend
  missingYoutubeCount?: number;
}

interface SearchResult {
  songTitle: string;
  songId?: number;
  youtubeLink?: string;
  matches: FileMatch[];
  hasMultipleMatches: boolean;
  bestMatch?: FileMatch;
  serviceInfo?: {
    service_id: number;
    liturgy_date: string;
    liturgy_name: string;
    season: string;
  };
}

interface FileMatch {
  fileName: string;
  fileUrl: string;
  fileType: string;
  songTitle: string;
  matchConfidence: number;
  fileSize?: number;
  lastModified?: string;
}

interface ServiceInfo {
  service_id: number;
  liturgy_date: string;
  liturgy_name: string;
  season: string;
  songCount: number;
}

const MissingFilesPage: React.FC = () => {
  const [weeksAhead, setWeeksAhead] = useState(4);
  const [searchType, setSearchType] = useState<'bulletin' | 'all'>('bulletin');
  const [results, setResults] = useState<MissingFilesResult | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleCheck = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await apiClient.post('/file-management/check-upcoming-songs', {
        weeksAhead,
        searchType
      });

      const data = response.data.data as MissingFilesResult;

      // Compute missing YouTube count from all results
      const allResults = [...data.found, ...data.questionable, ...data.missing];
      const missingYoutubeCount = allResults.filter(r => !r.youtubeLink).length;

      setResults({ ...data, missingYoutubeCount });
      console.log('Missing files check results:', data);
    } catch (err) {
      console.error('Error checking files:', err);
      setError('Failed to check files. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const getStatusIcon = (confidence?: number, hasMatches?: boolean) => {
    if (!hasMatches || !confidence) {
      return <XCircle size={16} className="text-danger" />;
    } else if (confidence >= 95) {
      return <CheckCircle size={16} className="text-success" />;
    } else {
      return <AlertTriangle size={16} className="text-warning" />;
    }
  };

  const getStatusText = (confidence?: number, hasMatches?: boolean) => {
    if (!hasMatches || !confidence) {
      return 'No file found';
    } else if (confidence >= 95) {
      return 'File found';
    } else {
      return `${confidence}% match`;
    }
  };

  const groupResultsByService = () => {
    if (!results) return [];

    const grouped: Map<number, {
      service: ServiceInfo;
      songs: Array<{
        title: string;
        result: SearchResult;
        status: 'found' | 'questionable' | 'missing';
      }>;
    }> = new Map();

    // Initialize services
    results.services.forEach(service => {
      grouped.set(service.service_id, {
        service,
        songs: []
      });
    });

    // Helper to add result to grouped map
    const addResult = (result: SearchResult, status: 'found' | 'questionable' | 'missing') => {
      if (result.serviceInfo) {
        const serviceGroup = grouped.get(result.serviceInfo.service_id);
        if (serviceGroup) {
          serviceGroup.songs.push({
            title: result.songTitle,
            result,
            status
          });
        }
      }
    };

    // Group results by their service
    results.found.forEach(r => addResult(r, 'found'));
    results.questionable.forEach(r => addResult(r, 'questionable'));
    results.missing.forEach(r => addResult(r, 'missing'));

    return Array.from(grouped.values());
  };

  return (
    <div className="app-container">
      <Navigation />
      <div className="main-content">
        <div className="container-fluid py-4">
          {/* Header */}
          <div className="d-flex justify-content-between align-items-center mb-4">
            <div>
              <h1 className="h3 mb-1">Missing Files for Sunday</h1>
              <p className="text-muted mb-0">
                Check which music files are available for upcoming services
              </p>
            </div>
            <Link to="/settings" className="btn btn-outline-secondary">
              <ArrowLeft size={16} className="me-2" />
              Back to Settings
            </Link>
          </div>

          {/* Configuration Panel */}
          <div className="card mb-4">
            <div className="card-body">
              <div className="row g-3 align-items-end">
                <div className="col-md-3">
                  <label className="form-label fw-medium">Weeks Ahead</label>
                  <select
                    className="form-select"
                    value={weeksAhead}
                    onChange={(e) => setWeeksAhead(parseInt(e.target.value))}
                  >
                    {[2, 4, 6, 8, 12].map(weeks => (
                      <option key={weeks} value={weeks}>{weeks} weeks</option>
                    ))}
                  </select>
                </div>

                <div className="col-md-4">
                  <label className="form-label fw-medium">Search Type</label>
                  <div className="btn-group w-100" role="group">
                    <input
                      type="radio"
                      className="btn-check"
                      name="searchType"
                      id="bulletin"
                      checked={searchType === 'bulletin'}
                      onChange={() => setSearchType('bulletin')}
                    />
                    <label className="btn btn-outline-primary" htmlFor="bulletin">
                      <FileText size={16} className="me-2" />
                      Bulletin Only
                    </label>

                    <input
                      type="radio"
                      className="btn-check"
                      name="searchType"
                      id="all"
                      checked={searchType === 'all'}
                      onChange={() => setSearchType('all')}
                    />
                    <label className="btn btn-outline-primary" htmlFor="all">
                      <Music size={16} className="me-2" />
                      All Scores
                    </label>
                  </div>
                </div>

                <div className="col-md-3">
                  <button
                    className="btn btn-primary w-100"
                    onClick={handleCheck}
                    disabled={isLoading}
                  >
                    {isLoading ? (
                      <>
                        <Loader size={16} className="me-2 spinner-border spinner-border-sm" />
                        Checking...
                      </>
                    ) : (
                      <>
                        <Search size={16} className="me-2" />
                        Check Files
                      </>
                    )}
                  </button>
                </div>
              </div>

              {error && (
                <div className="alert alert-danger mt-3 mb-0">
                  {error}
                </div>
              )}
            </div>
          </div>

          {/* Results */}
          {results && (
            <>
              {/* Quick Summary Table */}
              <div className="card mb-4">
                <div className="card-header bg-light">
                  <h5 className="mb-0">Quick Summary</h5>
                  <small className="text-muted">
                    Last checked: {new Date(results.summary.checkedAt).toLocaleString()}
                  </small>
                </div>
                <div className="card-body">
                  <div className="row text-center mb-3">
                    <div className="col">
                      <div className="p-3 border rounded">
                        <div className="h2 mb-1">{results.summary.totalChecked}</div>
                        <div className="text-muted small">Songs Checked</div>
                      </div>
                    </div>
                    <div className="col">
                      <div className="p-3 border rounded bg-success bg-opacity-10">
                        <div className="h2 mb-1 text-success">{results.summary.foundCount}</div>
                        <div className="text-muted small">Files Found</div>
                      </div>
                    </div>
                    <div className="col">
                      <div className="p-3 border rounded bg-warning bg-opacity-10">
                        <div className="h2 mb-1 text-warning">{results.summary.questionableCount}</div>
                        <div className="text-muted small">Questionable</div>
                      </div>
                    </div>
                    <div className="col">
                      <div className="p-3 border rounded bg-danger bg-opacity-10">
                        <div className="h2 mb-1 text-danger">{results.summary.missingCount}</div>
                        <div className="text-muted small">Files Missing</div>
                      </div>
                    </div>
                    <div className="col">
                      <div className="p-3 border rounded bg-danger bg-opacity-10">
                        <div className="h2 mb-1 text-danger">{results.missingYoutubeCount || 0}</div>
                        <div className="text-muted small">No YouTube</div>
                      </div>
                    </div>
                  </div>

                  {/* Service-by-Service Summary Table */}
                  <table className="table table-sm table-hover">
                    <thead>
                      <tr>
                        <th>Sunday</th>
                        <th>Song</th>
                        <th>File Status</th>
                        <th>File Name</th>
                        <th>YouTube</th>
                      </tr>
                    </thead>
                    <tbody>
                      {groupResultsByService().map(({ service, songs }) => (
                        <React.Fragment key={service.service_id}>
                          <tr className="table-secondary">
                            <td colSpan={5} className="fw-bold">
                              {new Date(service.liturgy_date).toLocaleDateString('en-US', {
                                weekday: 'long',
                                month: 'long',
                                day: 'numeric',
                                year: 'numeric'
                              })} - {service.liturgy_name}
                            </td>
                          </tr>
                          {songs.length > 0 ? (
                            songs.map((song, idx) => (
                              <tr key={idx}>
                                <td></td>
                                <td>
                                  {song.result.songId ? (
                                    <Link to={`/browse?tab=songs&songId=${song.result.songId}`} className="text-decoration-none">
                                      {song.title}
                                    </Link>
                                  ) : (
                                    song.title
                                  )}
                                </td>
                                <td>
                                  <span className="d-flex align-items-center gap-2">
                                    {getStatusIcon(song.result.bestMatch?.matchConfidence, song.result.matches.length > 0)}
                                    {getStatusText(song.result.bestMatch?.matchConfidence, song.result.matches.length > 0)}
                                  </span>
                                </td>
                                <td>
                                  {song.result.bestMatch ? (
                                    <span>
                                      {song.result.bestMatch.fileName}
                                      {song.result.hasMultipleMatches && ` (+${song.result.matches.length - 1} more)`}
                                    </span>
                                  ) : (
                                    <span className="text-muted fst-italic">No file found</span>
                                  )}
                                </td>
                                <td>
                                  {song.result.youtubeLink ? (
                                    <a
                                      href={song.result.youtubeLink}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="text-danger"
                                      title={song.result.youtubeLink}
                                    >
                                      <Youtube size={16} />
                                    </a>
                                  ) : (
                                    <span className="text-muted" title="No YouTube link">
                                      <XCircle size={16} />
                                    </span>
                                  )}
                                </td>
                              </tr>
                            ))
                          ) : (
                            <tr>
                              <td></td>
                              <td colSpan={4} className="text-muted fst-italic">No songs for this service</td>
                            </tr>
                          )}
                        </React.Fragment>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Detailed Cards - Coming soon in next iteration */}
              {(results.questionable.length > 0 || results.missing.length > 0) && (
                <div className="card">
                  <div className="card-header bg-light">
                    <h5 className="mb-0">Files Needing Attention</h5>
                  </div>
                  <div className="card-body">
                    {results.questionable.length > 0 && (
                      <div className="mb-4">
                        <h6 className="text-warning">
                          <AlertTriangle size={18} className="me-2" />
                          Questionable Matches ({results.questionable.length})
                        </h6>
                        <div className="list-group">
                          {results.questionable.map((result, idx) => (
                            <div key={idx} className="list-group-item">
                              <div className="fw-medium">{result.songTitle}</div>
                              <div className="small text-muted mt-1">
                                {result.matches.map((match, midx) => (
                                  <div key={midx}>
                                    {match.fileName} ({match.matchConfidence}%)
                                  </div>
                                ))}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {results.missing.length > 0 && (
                      <div>
                        <h6 className="text-danger">
                          <XCircle size={18} className="me-2" />
                          Missing Files ({results.missing.length})
                        </h6>
                        <div className="list-group">
                          {results.missing.map((result, idx) => (
                            <div key={idx} className="list-group-item">
                              <div className="fw-medium">{result.songTitle}</div>
                              <div className="small text-muted mt-1">No file found</div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default MissingFilesPage;
